/**
 * TEI (Theological Education Institution) manifest.
 *
 * One build serves several TEIs. Branding is resolved from REACT_APP_TEI at build time;
 * the identity (GET /api/tei) is loaded before the app renders (see src/index.js); field visibility,
 * features and labels (GET /api/tei/manifest) are loaded together with the current user (see auth.sagas.js),
 * before any authorised page renders, so every helper below is synchronous.
 *
 * Rules: anything TEI-specific goes through this module. Do not branch on the slug in components,
 * use hasFeature() / teiLabel() instead (field visibility is driven by permissions seeded from the manifest);
 * the slug is only for picking a branding entry
 * or a whole legacy view variant.
 */
import { axios } from '@Root/API/axios'
import ctfLogo from '@Root/assets/icons/ctf-logo.png'
import stHildLogo from '@Root/assets/icons/st-hild-logo.png'
import smcLogo from '@Root/assets/icons/SMC_Logo.png'
import trinityLogo from '@Root/assets/icons/TrinLogo.png'

// profile is the build-time fallback used only when GET /api/tei is unavailable.
const BRANDING = {
  ctf: { logo: ctfLogo, logoHeight: 50, profile: 'federation' },
  sthild: { logo: stHildLogo, logoHeight: 50, profile: 'federation' },
  stmellitus: { logo: smcLogo, logoHeight: 50, profile: 'college' },
  trinity: { logo: trinityLogo, logoHeight: 36, profile: 'college' },
}

export const TEI_SLUG = String(process.env.REACT_APP_TEI || '')
  .trim()
  .toLowerCase()

const EMPTY_MANIFEST = {
  slug: TEI_SLUG,
  name: '',
  short_name: '',
  profile: BRANDING[TEI_SLUG]?.profile || '',
  labels: {},
  optional: {},
  fields: {},
  features: {},
}

// What GET /api/tei returned; logout falls back to it.
let publicManifest = EMPTY_MANIFEST
let manifest = EMPTY_MANIFEST

// Public identity (slug, name, short_name), enough for the login and password recovery pages.
export const loadTei = async () => {
  const { data } = await axios.get('tei')
  publicManifest = manifest = { ...EMPTY_MANIFEST, ...data }
  return manifest
}

// Full manifest (labels, fields, features...), requires a token; loaded together with the user (see auth.sagas.js).
export const loadTeiManifest = async () => {
  const { data } = await axios.get('tei/manifest')
  manifest = { ...EMPTY_MANIFEST, ...data }
  return manifest
}

// Drops everything the public endpoint did not return on logout.
export const resetTei = () => {
  manifest = publicManifest
}

export const tei = () => manifest

export const teiBranding = () => BRANDING[manifest.slug] || BRANDING[TEI_SLUG] || BRANDING.sthild

export const hasFeature = name => Boolean(manifest.features[name])

export const teiLabel = (key, fallback = '') => manifest.labels[key] ?? fallback
